import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { api } from "../api/client";
import type { ConnectorRunRow, DataSourceRow, Meta } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import {
  ActiveChip,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  JsonField,
  LoadingState,
  Mono,
  PageHeader,
  RoleGate,
  SecretField,
  ago,
  when,
} from "../components/common";

/**
 * Data source administration.
 *
 * Credentials inside a config are handled by the API (nulled on read, preserved on
 * write); this screen's job is to make that visible rather than mysterious — hence
 * the separate secret inputs and the "stored, hidden" wording.
 */

interface DraftState {
  key: string;
  connectorType: string;
  configText: string;
  configValid: boolean;
  schedule: string;
  active: boolean;
  secrets: Record<string, string>;
}

const emptyDraft = (): DraftState => ({
  key: "",
  connectorType: "socrata",
  configText: "{\n  \n}",
  configValid: true,
  schedule: "",
  active: false,
  secrets: {},
});

export function SourcesPage() {
  const { can } = useAuth();
  const sources = useResource<{ items: DataSourceRow[] }>("/api/sources");
  const meta = useResource<Meta>("/api/meta");
  const [editing, setEditing] = useState<DataSourceRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DataSourceRow | null>(null);
  const [runsFor, setRunsFor] = useState<DataSourceRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const ingest = useMutation(async (source: DataSourceRow) => {
    await api.post(`/api/sources/${source.id}/ingest`);
  });
  const toggle = useMutation(async (source: DataSourceRow) => {
    await api.put(`/api/sources/${source.id}`, { active: !source.active });
  });
  const remove = useMutation(async (source: DataSourceRow) => {
    await api.del(`/api/sources/${source.id}?cascade=true`);
  });

  if (sources.loading && !sources.data) return <LoadingState label="Loading sources…" />;

  return (
    <Box>
      <PageHeader
        title="Data sources"
        subtitle="Connectors and their configuration. A new source is a row, not a deploy."
        actions={
          <RoleGate allowed={can("operator")} needs="operator">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              Add source
            </Button>
          </RoleGate>
        }
      />

      <ErrorBanner error={sources.error} onRetry={sources.reload} />
      <ErrorBanner error={ingest.error ?? toggle.error ?? remove.error} />

      {sources.data && sources.data.items.length === 0 ? (
        <EmptyState title="No data sources yet" hint="Add one, or run a seed script." />
      ) : null}

      {sources.data && sources.data.items.length > 0 ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Connector</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Cursor</TableCell>
                <TableCell>Last run</TableCell>
                <TableCell align="right">Signals</TableCell>
                <TableCell>Secrets</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.data.items.map((source) => (
                <TableRow key={source.id} hover>
                  <TableCell>
                    <Mono>{source.key}</Mono>
                  </TableCell>
                  <TableCell>{source.connectorType}</TableCell>
                  <TableCell>
                    <Mono>{source.schedule ?? "manual"}</Mono>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography variant="caption" noWrap display="block" title={source.cursor ?? ""}>
                      {source.cursor ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {source.lastRun ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={source.lastRun.status}
                          color={source.lastRun.status === "ok" ? "success" : "error"}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {ago(source.lastRun.finishedAt ?? source.lastRun.startedAt)}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{source.signalCount ?? 0}</TableCell>
                  <TableCell>
                    {source.redactedFields.length > 0 ? (
                      <Tooltip title={`Stored, never returned: ${source.redactedFields.join(", ")}`}>
                        <Chip label={`${source.redactedFields.length} stored`} variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        none
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <RoleGate allowed={can("operator")} needs="operator">
                      <Switch
                        checked={source.active}
                        size="small"
                        onChange={() => void toggle.run(source).then((ok) => ok && sources.reload())}
                      />
                    </RoleGate>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Recent runs">
                        <IconButton onClick={() => setRunsFor(source)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <RoleGate allowed={can("operator")} needs="operator">
                        <Tooltip title={source.active ? "Enqueue an ingest now" : "Activate the source first"}>
                          <IconButton
                            disabled={!source.active || ingest.pending}
                            onClick={() =>
                              void ingest.run(source).then((ok) => {
                                if (ok) setToast(`Ingest enqueued for ${source.key}`);
                              })
                            }
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </RoleGate>
                      <RoleGate allowed={can("operator")} needs="operator">
                        <IconButton onClick={() => setEditing(source)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </RoleGate>
                      <RoleGate allowed={can("owner")} needs="owner">
                        <IconButton color="error" onClick={() => setDeleting(source)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </RoleGate>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      {creating || editing ? (
        <SourceDialog
          source={editing}
          connectorTypes={meta.data?.connectorTypes ?? ["socrata", "ckan", "arcgis", "apify", "rest"]}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(message) => {
            setCreating(false);
            setEditing(null);
            setToast(message);
            sources.reload();
          }}
        />
      ) : null}

      {runsFor ? <RunsDialog source={runsFor} onClose={() => setRunsFor(null)} /> : null}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.key}?`}
        confirmLabel="Delete source and its data"
        pending={remove.pending}
        body={
          <>
            This deletes the source, every raw record it ingested, its run history and{" "}
            {deleting?.signalCount ?? 0} signal(s) built on it. Pausing the source is reversible; this is not.
          </>
        }
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          void remove.run(deleting).then((ok) => {
            if (!ok) return;
            setDeleting(null);
            setToast(`Deleted ${deleting.key}`);
            sources.reload();
          });
        }}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ""}
      />
    </Box>
  );
}

/**
 * Create/edit form.
 *
 * Secrets are separate inputs rather than fields in the JSON, because the JSON the
 * console holds has `null` where a credential is stored — round-tripping it as-is
 * is the correct thing to do, and typing over that null would be the operator
 * "helpfully" deleting a live token.
 */
function SourceDialog({
  source,
  connectorTypes,
  onClose,
  onSaved,
}: {
  source: DataSourceRow | null;
  connectorTypes: string[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [draft, setDraft] = useState<DraftState>(emptyDraft());

  useEffect(() => {
    if (!source) {
      setDraft(emptyDraft());
      return;
    }
    setDraft({
      key: source.key,
      connectorType: source.connectorType,
      configText: JSON.stringify(source.config, null, 2),
      configValid: true,
      schedule: source.schedule ?? "",
      active: source.active,
      secrets: Object.fromEntries(source.redactedFields.map((f) => [f, ""])),
    });
  }, [source]);

  const save = useMutation(async () => {
    const config = JSON.parse(draft.configText) as Record<string, unknown>;
    // Only top-level secret names are offered as inputs; a nested one (rest's
    // auth.token) is edited in the JSON itself.
    for (const [path, value] of Object.entries(draft.secrets)) {
      if (value !== "" && !path.includes(".")) config[path] = value;
    }
    const body = {
      key: draft.key.trim(),
      connectorType: draft.connectorType,
      config,
      schedule: draft.schedule.trim() === "" ? null : draft.schedule.trim(),
      active: draft.active,
    };
    if (source) await api.put(`/api/sources/${source.id}`, body);
    else await api.post("/api/sources", body);
  });

  const submittable =
    draft.key.trim() !== "" && draft.configValid && !save.pending && draft.configText.trim() !== "";

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{source ? `Edit ${source.key}` : "Add data source"}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Key"
              value={draft.key}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              helperText="Lowercase, digits and underscores. Stable — signals reference it."
              fullWidth
            />
            <TextField
              select
              label="Connector"
              value={draft.connectorType}
              onChange={(e) => setDraft({ ...draft, connectorType: e.target.value })}
              sx={{ minWidth: 160 }}
            >
              {connectorTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Schedule (cron, UTC)"
            value={draft.schedule}
            onChange={(e) => setDraft({ ...draft, schedule: e.target.value })}
            helperText="Empty means manual only. A changed cron takes effect on the next service restart."
            fullWidth
          />

          <JsonField
            label="Config"
            value={draft.configText}
            onChange={(configText, configValid) => setDraft({ ...draft, configText, configValid })}
            helperText="Validated against the connector's schema on save."
          />

          {Object.keys(draft.secrets).length > 0 ? (
            <>
              <Alert severity="info">
                These credentials are stored and never sent to the browser. Leave a field blank to keep the
                stored value.
              </Alert>
              {Object.keys(draft.secrets).map((path) => (
                <SecretField
                  key={path}
                  label={path}
                  value={draft.secrets[path] ?? ""}
                  isSet
                  onChange={(value) => setDraft({ ...draft, secrets: { ...draft.secrets, [path]: value } })}
                  helperText={
                    path.includes(".")
                      ? "Nested credential — edit it in the JSON above; this field is informational."
                      : undefined
                  }
                />
              ))}
            </>
          ) : null}

          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            <Typography variant="body2">Active (scheduled ingests run)</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!submittable}
          onClick={() =>
            void save.run(undefined).then((ok) => ok && onSaved(source ? `Saved ${draft.key}` : `Created ${draft.key}`))
          }
        >
          {save.pending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RunsDialog({ source, onClose }: { source: DataSourceRow; onClose: () => void }) {
  const runs = useResource<{ items: ConnectorRunRow[] }>(`/api/sources/${source.id}/runs?limit=25`);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Recent runs — {source.key}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={runs.error} onRetry={runs.reload} />
        {runs.loading && !runs.data ? <LoadingState /> : null}
        {runs.data ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell>Cursor after</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.data.items.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>{when(run.startedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={run.status}
                      color={run.status === "ok" ? "success" : run.status === "error" ? "error" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{run.rows ?? "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="caption" noWrap display="block">
                      {run.cursorAfter ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="caption" color="error.main" sx={{ whiteSpace: "normal" }}>
                      {run.error ?? ""}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {runs.data.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>This source has never run.</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
